import styled from "@emotion/styled";
import PropTypes from "prop-types";
import React, { forwardRef, useRef, useState } from "react";
import { kebabCase } from "lodash";

import PlusIcon from "~/components/PlusIcon";

import useOnClickOutside from "~/hooks/useOnClickOutside";
import useOnKeyPress from "~/hooks/useOnKeyPress";
import useStringTranslation from "~/hooks/translations/useStringTranslation";

import { fadeIn } from "~/styles/animations";

import transition from "~/utilities/transition";
import {
    sanitizeAndParseHTML,
    KEYCODE_TAB,
    KEYCODE_ESCAPE,
    KEYCODE_SPACE,
    KEYCODE_ENTER,
    keyboardClick,
} from "~/utilities/helpers";

import { generateDropdownFilterElementProps } from "~/utilities/filters";

const Wrapper = styled.div`
    position: ${({ isOpen }) => (isOpen ? `relative` : `static`)};
    display: block;
    width: 100%;
    z-index: ${({ isOpen }) => (isOpen ? 5 : 0)};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobilePortrait}) {
        min-width: ${({ singleSelect }) => (singleSelect ? `330px` : `215px`)};
        display: flex;
        flex-direction: column;
    }

    @media (max-width: ${({ theme }) =>
            theme.table.breakpoints.filterColumnWrap}) {
        width: 100% !important;
    }
`;

const Trigger = styled.button`
    ${transition({}, "color")};
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.0075em;
    border: ${({ theme }) => `1px solid ${theme.palette.black}`};
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 15px;
    width: 100%;
    height: 50px;
    min-height: ${({ minHeight }) => (minHeight ? minHeight : "auto")};
    cursor: ${({ disabled }) => (disabled ? `not-allowed` : `pointer`)};
    text-align: left;
    color: ${({ theme }) => theme.colors.typography.links};
    transition:
        background-color 200ms ease-in-out,
        color 200ms ease-in-out;

    &:hover,
    &:focus {
        color: ${({ theme }) => theme.palette.darkTeal};
        background-color: ${({ theme }) => theme.palette.grayFive};
    }

    @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
        font-size: 2rem;
    }
`;

const TriggerText = styled.span`
    margin-right: 10px;
`;

const Content = styled.ul`
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    border-left: ${({ theme }) => `1px solid ${theme.palette.black}`};
    border-right: ${({ theme }) => `1px solid ${theme.palette.black}`};
    border-bottom: ${({ theme }) => `1px solid ${theme.palette.black}`};
    padding: 10px
        ${({ showAsCheckbox, singleSelect }) =>
            showAsCheckbox || singleSelect ? "0" : ""};
    background-color: ${({ theme }) => theme.palette.white};
    z-index: 10;
    display: ${({ isOpen }) => (isOpen ? `block` : `none`)};
    animation: ${fadeIn} 0.25s ease;
    max-height: 300px;
    overflow-y: auto;
`;

const Fieldset = Content.withComponent("fieldset");

const OptionInput = styled.input`
    width: 0;
    height: 0;
    padding: 0;
    margin: 0;
    opacity: 0;
`;

const Option = styled.label`
    ${transition({}, "color")};
    font-size: 1.8rem;
    letter-spacing: -0.008em;
    line-height: 1.2;
    color: ${({ theme }) => theme.palette.black};
    display: block;
    text-align: left;
    cursor: pointer;
    color: ${({ theme, selected }) =>
        selected ? theme.colors.typography.linksHover : "normal"};

    &:not(:last-of-type) {
        margin-bottom: 12px;
    }

    &:hover {
        color: ${({ theme }) => theme.colors.typography.linksHover};
    }
`;

const SelectItem = styled.li`
    padding: 10px 10px 10px 0;

    &:hover,
    &:focus-within {
        cursor: pointer;
        background-color: ${({ theme, disabled }) =>
            disabled ? "none" : theme.palette.grayFive};
    }

    & > span.radio::after,
    & > span.radio::before {
        border-radius: 50%;
    }

    & > span.radio:not(.checked)::after,
    & > button[aria-pressed="false"] > .checkmark::after {
        content: "";
        width: 22px;
        display: inline-block;
        aspect-ratio: 1;
        border: 1px solid
            ${({ disabled, theme }) =>
                disabled ? theme.palette.graySix : theme.palette.black};
        margin: -5px 10px;
        background-color: ${({ disabled, theme }) =>
            disabled ? theme.palette.graySix : theme.palette.white};
    }

    & > span.radio.checked::before,
    & > button[aria-pressed="true"] > .checkmark::before {
        content: "";
        width: 22px;
        display: inline-block;
        aspect-ratio: 1;
        margin: -6px 10px;
    }

    & > button[aria-pressed="true"] > .checkmark::before {
        border: 1px solid ${({ theme }) => theme.palette.black};
        background-color: ${({ theme }) => theme.palette.purple};
    }

    & > span.radio.checked::before {
        border: 6px solid ${({ theme }) => theme.palette.purple};
        background-color: ${({ theme }) => theme.palette.white};
    }

    & > button[aria-pressed="true"] > .checkmark::after {
        content: "";
        position: absolute;
        display: block;
        width: 8px;
        height: 16px;
        margin-left: 0.6rem;
        border: solid white;
        border-width: 0 3px 3px 0;
        -webkit-transform: rotate(45deg) translate(-7px, -23px);
        -ms-transform: rotate(45deg) translate(-7px, -23px);
        transform: rotate(45deg) translate(-7px, -23px);
    }
`;

const RadioWrapper = SelectItem.withComponent("div");

const SelectOption = styled.button`
    width: max-content;
    display: flex;
    justify-content: flex-start;
`;

const SelectRadio = styled.input`
    position: absolute;
    opacity: 0;
    height: 0;
    width: 0;
    margin-left: 5px;
`;

const InputLabel = styled.label`
    cursor: pointer;
    margin-left: 1rem;
    line-height: 2.5rem;
    font-size: 2rem;
    color: ${({ disabled, theme }) =>
        disabled ? theme.palette.graySix : "inherit"};
`;

const MarkElement = styled.span``;

const DropdownFilter = forwardRef(
    (
        {
            label,
            staticLabel = "",
            languageCode,
            options,
            selectedOptions = [],
            showAsCheckbox = false,
            singleSelect,
            minHeight = "",
            disabled = false,
            onLastOptionBlur = () => {},
        },
        ref,
    ) => {
        const [isOpen, setIsOpen] = useState(false);
        const contentRef = useRef();
        const lastOptionRef = useRef();
        const { filtersAppliedText } = useStringTranslation([
            { text: "filters applied", handle: "filtersAppliedText" },
        ]);

        const handleClick = () => setIsOpen(!isOpen);

        useOnClickOutside(ref, () => {
            setIsOpen(false);
        });

        useOnKeyPress(
            KEYCODE_SPACE,
            singleSelect && isOpen ? keyboardClick : () => {},
        );
        useOnKeyPress(
            KEYCODE_ENTER,
            singleSelect && isOpen ? keyboardClick : () => {},
        );

        useOnKeyPress(KEYCODE_TAB, (keyboardEvent) => {
            if (
                isOpen &&
                ((keyboardEvent.shiftKey &&
                    keyboardEvent.target === ref.current.firstElementChild) ||
                    (!keyboardEvent.shiftKey &&
                        lastOptionRef.current?.contains(
                            keyboardEvent.target,
                        )) ||
                    (singleSelect &&
                        Array.from(contentRef.current.children).includes(
                            keyboardEvent.target,
                        )))
            ) {
                keyboardEvent.preventDefault();
                setIsOpen(false);
                onLastOptionBlur();
            }
        });

        useOnKeyPress(KEYCODE_ESCAPE, () => {
            if (isOpen) {
                setIsOpen(false);
                ref.current.firstElementChild.focus();
            }
        });

        const triggerText = `${label ? label : null}${
            selectedOptions.length && !singleSelect
                ? `<span aria-label=" ${selectedOptions.length} ${filtersAppliedText}">(${selectedOptions.length})</span>`
                : ``
        }`;

        const parsedText = sanitizeAndParseHTML(triggerText);

        const buttonID = kebabCase(staticLabel ?? label);

        const DropdownItems = singleSelect ? Fieldset : Content;

        return (
            <Wrapper {...{ ref, isOpen, singleSelect }}>
                <Trigger
                    {...{ isOpen, disabled, minHeight }}
                    type="button"
                    id={buttonID}
                    aria-expanded={isOpen}
                    aria-controls={parsedText}
                    onClick={handleClick}
                >
                    <TriggerText lang={languageCode}>{parsedText}</TriggerText>
                    <PlusIcon open={isOpen} />
                </Trigger>
                <DropdownItems
                    aria-labelledby={buttonID}
                    {...{ isOpen, showAsCheckbox, singleSelect }}
                    className="unstyled-list"
                    ref={contentRef}
                >
                    {options.map((option, index) => {
                        const {
                            optionProps,
                            inputProps,
                            markProps,
                            labelText,
                        } = generateDropdownFilterElementProps(
                            {
                                option,
                                selectedOptions,
                                showAsCheckbox,
                                singleSelect,
                                options,
                                lastOptionRef,
                                dropdownLabel: staticLabel ?? label,
                            },
                            index,
                        );

                        return showAsCheckbox ? (
                            <SelectItem key={optionProps.key}>
                                <SelectOption {...optionProps}>
                                    <MarkElement {...markProps} />
                                    {labelText}
                                </SelectOption>
                            </SelectItem>
                        ) : singleSelect ? (
                            <RadioWrapper {...optionProps}>
                                <SelectRadio
                                    {...inputProps}
                                    id={`radio-input-${index}-${kebabCase(labelText)}`}
                                />
                                <MarkElement {...markProps} />
                                <InputLabel
                                    htmlFor={`radio-input-${index}-${kebabCase(labelText)}`}
                                    disabled={option.isDisabled}
                                    onClick={(clickEvent) =>
                                        clickEvent.preventDefault()
                                    }
                                >
                                    {labelText}
                                </InputLabel>
                            </RadioWrapper>
                        ) : (
                            <SelectItem key={optionProps.key}>
                                <Option {...optionProps}>
                                    <OptionInput {...inputProps} />
                                    {labelText}
                                </Option>
                            </SelectItem>
                        );
                    })}
                </DropdownItems>
            </Wrapper>
        );
    },
);

DropdownFilter.propTypes = {
    disabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    staticLabel: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            action: PropTypes.func.isRequired,
        }).isRequired,
    ).isRequired,
    selectedOptions: PropTypes.array,
    showAsCheckbox: PropTypes.bool,
    minHeight: PropTypes.string,
    onLastOptionBlur: PropTypes.func,
};

export default DropdownFilter;
